/**
 * Deliberately does NOT include every real `.inp` `[OPTIONS]` keyword —
 * only ones this port's own engine genuinely reads and acts on. Exposing a
 * control for one the engine hardcodes regardless (e.g. INERTIAL_DAMPING)
 * would be misleading.
 *
 * `minSlope` applies to BOTH routing methods; every other Dynamic-Wave
 * field below is still shown regardless of the currently-picked
 * `flowRouting`, so switching methods later doesn't silently lose whatever
 * was already configured here.
 */
export interface ProjectOptionsForm {
  flowUnits: string;
  flowRouting: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  reportStartDate: string;
  reportStartTime: string;
  /** A bare number is DECIMAL HOURS, not seconds — a genuinely different convention than `routingStep` below. */
  reportStep: string;
  wetStep: string;
  /** Silently clamped UP to `wetStep` if set smaller — a value shorter than Wet weather step won't actually take effect as typed. */
  dryStep: string;
  /** A bare number is SECONDS directly — see `reportStep`'s own note on why this differs. */
  routingStep: string;
  /** Percent (e.g. "0.5", not "0.005"). Empty means "use the engine's own 0 default" (no minimum). */
  minSlope: string;
  /** "YES" or "NO" exactly — matches the real `.inp` keyword's own values. */
  allowPonding: string;
  /** "SLOPE" | "FROUDE" | "BOTH" | "NONE" — the real `.inp`'s own 4 keywords. */
  normalFlowLimited: string;
  /** 0-2, 0 disables variable time-stepping entirely. Empty means the engine's own 0.75 default. */
  variableStep: string;
  /** Seconds. Empty means the engine's own 0.5s default. */
  minimumStep: string;
  /** A positive integer. Empty means the engine's own default of 8. */
  maxTrials: string;
  /** The project's own length unit — NOT always feet. Empty means the engine's own 0.005ft default. */
  headTolerance: string;
  /** The project's own length unit squared. Empty (or "0") means the engine's own ~12.566ft² default. */
  minSurfArea: string;
  /** 0/empty disables conduit lengthening entirely. */
  lengtheningStep: string;
}

const FIELD_TO_KEY: Record<keyof ProjectOptionsForm, string> = {
  flowUnits: "FLOW_UNITS",
  flowRouting: "FLOW_ROUTING",
  startDate: "START_DATE",
  startTime: "START_TIME",
  endDate: "END_DATE",
  endTime: "END_TIME",
  reportStartDate: "REPORT_START_DATE",
  reportStartTime: "REPORT_START_TIME",
  reportStep: "REPORT_STEP",
  wetStep: "WET_STEP",
  dryStep: "DRY_STEP",
  routingStep: "ROUTING_STEP",
  minSlope: "MIN_SLOPE",
  allowPonding: "ALLOW_PONDING",
  normalFlowLimited: "NORMAL_FLOW_LIMITED",
  variableStep: "VARIABLE_STEP",
  minimumStep: "MINIMUM_STEP",
  maxTrials: "MAX_TRIALS",
  headTolerance: "HEAD_TOLERANCE",
  minSurfArea: "MIN_SURFAREA",
  lengtheningStep: "LENGTHENING_STEP",
};

/** Every numeric Dynamic-Wave tuning field defaults to `""`, not a literal number — an untouched field must never silently pin the project to a value the user never actually chose. */
export const DEFAULT_PROJECT_OPTIONS: ProjectOptionsForm = {
  flowUnits: "CFS",
  flowRouting: "KINWAVE",
  startDate: "",
  startTime: "00:00:00",
  endDate: "",
  endTime: "00:00:00",
  reportStartDate: "",
  reportStartTime: "00:00:00",
  reportStep: "00:15:00",
  wetStep: "00:05:00",
  dryStep: "01:00:00",
  routingStep: "00:00:20",
  minSlope: "",
  allowPonding: "NO",
  normalFlowLimited: "BOTH",
  variableStep: "",
  minimumStep: "",
  maxTrials: "",
  headTolerance: "",
  minSurfArea: "",
  lengtheningStep: "00:00:00",
};

/** Case-insensitive key match; `undefined` if that row doesn't exist at all. */
export function findOption(raw: ReadonlyMap<string, string[][]>, key: string): string | undefined {
  return (raw.get("OPTIONS") ?? []).find((row) => row[0]?.toUpperCase() === key)?.[1];
}

// Position `i` of both tables resolves to the same routing choice. Real
// SWMM matches by PREFIX, case-insensitive, not exact equality — replicated
// here via `startsWith`. XKINWAVE/EKW ("extended kinematic wave") both fold
// to plain KINWAVE — the real engine never actually distinguished the two.
const MODERN_ROUTE_KEYWORDS = ["NONE", "STEADY", "KINWAVE", "XKINWAVE", "DYNWAVE"] as const;
const LEGACY_ROUTE_KEYWORDS = ["NONE", "NF", "KW", "EKW", "DW"] as const;
const RESOLVED_ROUTE_METHOD = ["NONE", "STEADY", "KINWAVE", "KINWAVE", "DYNWAVE"] as const;

/** Must recognize the real engine's own legacy 2/3-letter abbreviations (NF/KW/EKW/DW) too, not just the modern keywords — a real `.inp` declaring legacy `FLOW_ROUTING DW` would otherwise silently fall through to the wrong routing method entirely, with no warning. */
export function resolveFlowRoutingMethod(
  raw: ReadonlyMap<string, string[][]>
): "NONE" | "STEADY" | "KINWAVE" | "DYNWAVE" | undefined {
  const value = findOption(raw, "FLOW_ROUTING")?.trim().toUpperCase();
  if (!value) return undefined;
  const modernIndex = MODERN_ROUTE_KEYWORDS.findIndex((w) => value.startsWith(w));
  const index =
    modernIndex !== -1 ? modernIndex : LEGACY_ROUTE_KEYWORDS.findIndex((w) => value.startsWith(w));
  return index === -1 ? undefined : RESOLVED_ROUTE_METHOD[index];
}

export function readProjectOptions(raw: ReadonlyMap<string, string[][]>): ProjectOptionsForm {
  const result = { ...DEFAULT_PROJECT_OPTIONS };
  for (const field of Object.keys(FIELD_TO_KEY) as (keyof ProjectOptionsForm)[]) {
    const value = findOption(raw, FIELD_TO_KEY[field]);
    if (value !== undefined) result[field] = value;
  }
  return result;
}

/** A field left blank still gets WRITTEN as an empty value rather than skipped, so a stale row from a previous edit can't linger. */
export function writeProjectOptions(
  raw: ReadonlyMap<string, string[][]>,
  form: ProjectOptionsForm
): Map<string, string[][]> {
  const rows = (raw.get("OPTIONS") ?? []).map((row) => [...row]);
  for (const field of Object.keys(FIELD_TO_KEY) as (keyof ProjectOptionsForm)[]) {
    const key = FIELD_TO_KEY[field];
    const value = form[field];
    const index = rows.findIndex((row) => row[0]?.toUpperCase() === key);
    if (index === -1) rows.push([key, value]);
    else rows[index] = [key, value];
  }
  const next = new Map(raw);
  next.set("OPTIONS", rows);
  return next;
}
